import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import {
  PROJECTS_ON_DOCUMENT_LABEL,
  projectDisplayName,
  type CompanyProject,
} from '@/lib/company-projects';

/**
 * The projects a DEAL, QUOTE or INVOICE names on its line items, listed in full.
 *
 * Owner's decision, 2026-09-14, on their report that the column did not open on
 * click the way the Project of interest column beside it does: it opens **a
 * read-only list**, chosen over wiring the company's editor to it.
 *
 * ## 🔴 IT WRITES NOTHING, AND THAT IS THE DECISION RATHER THAN AN UNFINISHED HALF
 *
 * ⛔ **Do not give this dialog a picker, a Save, or an `onSave`.** Two separate
 * things forbid it, and only the first is obvious:
 *
 *  1. **There is nothing on a document to write.** A line's project is a property
 *     of the LINE — chosen per row on the quote, invoice and deal forms — and
 *     this list is a DISTINCT summary across them. No single control can express
 *     "move these four lines onto another project", and a control that edited the
 *     first line silently would be worse than none.
 *  2. ⛔ **The editor that DOES exist is the wrong one and is irreversible.**
 *     `CompanyProjectsModal` writes the COMPANY's projects of interest — a
 *     different list — and since 2026-09-13 removing one **closes that sales
 *     pursuit as Lost, permanently, with nothing recording why**, so a reopen
 *     cannot tell a removal from a declined quote. Opened from here it would let
 *     somebody untick an item from a list they were never shown, with a `200` and
 *     no undo. That was put to the owner as an option on 2026-09-14 **and was not
 *     the one chosen.**
 *
 * ⚠️ **So the way to change what a document is for is to edit its LINE ITEMS**,
 * on the document itself. Nothing in this dialog may say so — that is helper text
 * attached to a control — which is exactly why it must not grow a disabled
 * picker either: an affordance whose only outcome is nothing is worse than no
 * affordance, and this page has had a menu reported as a DELETED FEATURE for
 * being merely unreachable.
 *
 * ## ⚠️ WHAT IT IS FOR: the cell CANNOT show the whole list, and measurably so
 *
 * The cell caps at **two names** then a `+N` marker — a cap derived from a 12rem
 * column, not from the data — while measured 2026-09-14 **~21% of quotes and
 * ~12% of invoices name three or more line projects, up to 8**. The only complete
 * reading was `companyProjectsTitle` on the cell's `title` attribute, and **a
 * `title` does not exist on a touch device at all**, so on a phone the shortfall
 * was unrecoverable. This dialog is that recovery path, reachable by the gesture
 * a reader already tried.
 *
 * ⚠️ **Each row LINKS to the project**, which is the second thing the cell could
 * not do: a truncated name in a 12rem column is not a way to reach the record.
 *
 * ## ⚠️ ONE MOUNT PER PAGE, holding the row's projects
 *
 * The same rule as `CompanyProjectsModal`, `ActivityDrawer` and the four create
 * forms above it: five cards of a hundred rows would otherwise be five hundred
 * mounted dialogs, each with its own focus trap.
 *
 * ⚠️ **`open` is ANDed with a non-empty list**, the same idiom
 * `CompanyProjectsModal` uses for its `hasCompany`. It is belt-and-braces —
 * `DocumentProjectsCell` renders no trigger at all for an empty or unread list,
 * because a dialog that opens to say nothing is the affordance-with-one-outcome
 * above — and it is what stops a later call site reaching that state by a route
 * the cell does not guard.
 *
 * ⚠️ **`size="md"` matches `CompanyProjectsModal` deliberately.** A reader meets
 * both dialogs over the same column on one page, and two different widths read as
 * two different kinds of thing rather than as one column answered two ways.
 */
export interface DocumentProjectsModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * The record these projects belong to, for the title.
   *
   * ⚠️ **The row's own label, not the company's.** Five cards of a hundred rows
   * sit behind this dialog, so the title is the only thing saying which document
   * was opened — and on these three cards the company is NOT the subject.
   */
  documentLabel: string;
  /**
   * ⚠️ **Non-nullable, and the caller resolves `null` before it gets here.**
   * `PartnershipRow.lineProjects` is nullable because an unread list is not an
   * empty one, and folding the two here would be the `?? []` this column's whole
   * contract refuses — on the one field whose job is to say what a document
   * covers.
   */
  projects: readonly CompanyProject[];
}

export function DocumentProjectsModal({
  open,
  onClose,
  documentLabel,
  projects,
}: DocumentProjectsModalProps) {
  return (
    <Modal
      open={open && projects.length > 0}
      onClose={onClose}
      /*
       * ⚠️ **The heading constant, never a second spelling.**
       * `company-projects.ts` keeps this word in one place precisely so a dialog
       * over the data cannot start calling it something the column does not.
       */
      title={`${PROJECTS_ON_DOCUMENT_LABEL} — ${documentLabel}`}
      size="md"
      /*
       * ⚠️ **Backdrop dismissal stays ON, unlike `CompanyProjectsModal`.** That
       * flag is turned off where a clipped backdrop click would cost somebody a
       * half-made selection; there is nothing to lose here, and making a
       * read-only dialog harder to leave than an editing one is backwards.
       */
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {/*
        * ⚠️ **No count line and no `description`.** The list is right here and
        * tops out at 8, so a "3 projects" line above three rows states what the
        * reader is already looking at — and a `Modal` `description` is helper
        * text, which this app prohibits outright.
        */}
      <ul className="flex flex-col gap-0.5">
        {projects.map((project) => (
          <li key={project.id}>
            <Link
              to={`/projects/${project.id}`}
              className="block rounded-sm px-2 py-1.5 text-sm text-fg transition-colors duration-150 ease-out hover:bg-surface-hover motion-reduce:transition-none"
            >
              {/*
                * ⚠️ **`projectDisplayName`, never `project.name`.** It carries
                * the `#id` fallback for a blank name, which is the difference
                * between an identifiable row and an empty clickable strip.
                */}
              {projectDisplayName(project)}
            </Link>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
